import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Rating,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const ProductQuickView = ({
  open,
  onClose,
  product,
  context,
  wishlist = [],
  onAddToCart,
  onToggleWishlist,
  getImageUrl,
  getAverageRating,
  getReviewCount,
}) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset image index when product changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product]);

  // Keyboard navigation for image slider
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!open || !product?.Default_Media || product.Default_Media.length <= 1)
        return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPreviousImage();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goToNextImage();
      }
    };

    if (open) {
      window.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [open, product?.Default_Media?.length]);

  // Navigation functions for image slider
  const goToPreviousImage = () => {
    if (product?.Default_Media && product.Default_Media.length > 1) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === 0 ? product.Default_Media.length - 1 : prevIndex - 1
      );
    }
  };

  const goToNextImage = () => {
    if (product?.Default_Media && product.Default_Media.length > 1) {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === product.Default_Media.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  // Removed auto-rotation to keep static display

  // Check if product is in wishlist
  const isInWishlist = (product) => {
    return wishlist.some((item) => item.Product_Id === product?.Product_Id);
  };

  // Handle combination rendering
  const renderCombinationChip = (combinationText, isDefault = false) => {
    if (!combinationText) return "Default";

    // Parse combination text and create a readable format
    const parts = combinationText.split(" | ");
    if (parts.length > 1) {
      return parts
        .map((part) => {
          const [key, value] = part.split(": ");
          return value || key;
        })
        .join(", ");
    }
    return combinationText;
  };

  // Helper functions for combination features - matching ProductCard.jsx logic
  const getOtherCombinations = () => {
    if (
      !product.other_combinations ||
      product.other_combinations.length === 0
    ) {
      return [];
    }
    return product.other_combinations;
  };

  const getAllCombinations = () => {
    const allCombos = [];
    if (product.Default_Combination?.Combination_Text) {
      allCombos.push(product.Default_Combination.Combination_Text);
    }
    if (product.other_combinations && product.other_combinations.length > 0) {
      allCombos.push(...product.other_combinations);
    }
    return allCombos;
  };

  // Enhanced tooltip content with chip-based combinations display
  const getCombinationTooltipContent = () => {
    const otherCombos = getOtherCombinations();
    if (otherCombos.length === 0) return "Only one combination available";

    return (
      <Box sx={{ maxWidth: 300 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
          {otherCombos.map((combo, index) => (
            <Chip
              key={index}
              label={combo}
              size="small"
              sx={{
                fontSize: "0.7rem",
                height: 24,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(255, 255, 255, 0.9)",
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(0, 0, 0, 0.8)",
                border: "1px solid",
                borderColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.1)",
              }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          overflow: "hidden",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(18, 18, 20, 0.98)"
              : "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(20px)",
          border: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.06)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 24px 48px rgba(0, 0, 0, 0.6)"
              : "0 24px 48px rgba(0, 0, 0, 0.15)",
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: "95vh", sm: "90vh" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1.5,
          pt: 2,
          px: { xs: 2, sm: 3 },
          borderBottom: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.06)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Quick View
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": {
              color: "primary.main",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ p: { xs: 2, sm: 3 }, maxHeight: "70vh", overflowY: "auto" }}
      >
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 3 },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Product Image Section */}
          <Box
            sx={{
              flex: { xs: "1 1 auto", md: "0 0 280px" },
              height: { xs: 250, md: 280 },
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.02)"
                  : "rgba(0, 0, 0, 0.02)",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.06)",
              position: "relative",
            }}
          >
            <Box
              component="img"
              src={getImageUrl ? getImageUrl(product, currentImageIndex) : ""}
              alt={product.Product_Full_Name}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            {/* Navigation arrows - only show if there are multiple images */}
            {product?.Default_Media && product.Default_Media.length > 1 && (
              <>
                {/* Left arrow */}
                <IconButton
                  onClick={goToPreviousImage}
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    color: "white",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      transform: "translateY(-50%) scale(1.1)",
                    },
                    transition: "all 0.3s ease",
                    zIndex: 10,
                  }}
                >
                  <ArrowBackIosIcon fontSize="small" />
                </IconButton>

                {/* Right arrow */}
                <IconButton
                  onClick={goToNextImage}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    color: "white",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                      transform: "translateY(-50%) scale(1.1)",
                    },
                    transition: "all 0.3s ease",
                    zIndex: 10,
                  }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </>
            )}

            {/* Discount badge */}
            {product?.Default_Inventory?.Total_MRP_Price &&
              product.Default_Inventory.Total_MRP_Price !==
                product?.Default_Inventory?.Total_Selling_Price && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 10,
                    backgroundColor: "rgba(255, 87, 34, 0.95)",
                    color: "white",
                    padding: "8px 12px",
                    borderRadius: "20px",
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    backdropFilter: "blur(8px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  }}
                >
                  {Math.round(
                    ((product.Default_Inventory.Total_MRP_Price -
                      product.Default_Inventory.Total_Selling_Price) /
                      product.Default_Inventory.Total_MRP_Price) *
                      100
                  )}
                  % OFF
                </Box>
              )}

            {/* Image indicators - clickable for manual navigation */}
            {product?.Default_Media && product.Default_Media.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 0.5,
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  backdropFilter: "blur(8px)",
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                {product.Default_Media.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor:
                        index === currentImageIndex
                          ? "white"
                          : "rgba(255, 255, 255, 0.5)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "white",
                        transform: "scale(1.2)",
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Product Details Section */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {/* Header */}
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 1, lineHeight: 1.2 }}
              >
                {product.Product_Full_Name}
              </Typography>

              {/* Vendor and Categories */}
              <Box sx={{ display: "flex", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
                {context.state?.companyInfo?.Is_Aggregator === "1" &&
                  product.Vendor_Name && (
                    <Chip
                      label={product.Vendor_Name}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  )}
              </Box>
            </Box>

            {/* Ratings */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Rating
                value={
                  getAverageRating
                    ? getAverageRating(product)
                    : product.averageRating || 0
                }
                precision={0.5}
                readOnly
                size="small"
              />
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "0.8rem" }}
              >
                {getAverageRating
                  ? `${getAverageRating(product)} (${getReviewCount(product)})`
                  : `${(product.averageRating || 0).toFixed(1)} (${
                      product.reviewCount || 0
                    })`}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.5,
                fontSize: "0.85rem",
              }}
            >
              {product.Short_Description || "No description available."}
            </Typography>

            {/* Combinations */}
            {product.Default_Combination?.Combination && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, fontSize: "0.85rem" }}
                >
                  Options ({getAllCombinations().length})
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Tooltip
                    title={
                      getOtherCombinations().length > 0
                        ? getCombinationTooltipContent()
                        : "Only one combination available"
                    }
                    placement="top"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 320,
                          fontSize: "0.8rem",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(40, 40, 40, 0.98)"
                              : "rgba(60, 60, 60, 0.98)",
                          backdropFilter: "blur(12px)",
                          border: (theme) =>
                            theme.palette.mode === "dark"
                              ? "1px solid rgba(255, 255, 255, 0.1)"
                              : "1px solid rgba(255, 255, 255, 0.2)",
                          borderRadius: "8px",
                          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                          p: 1.5,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        cursor: "pointer",
                        position: "relative",
                        "&:hover": {
                          transform: "scale(1.02)",
                        },
                      }}
                    >
                      <Chip
                        label={renderCombinationChip(
                          product.Default_Combination.Combination,
                          true
                        )}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          height: 24,
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.06)",
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.9)"
                              : "rgba(0, 0, 0, 0.8)",
                          border: "1px solid",
                          borderColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.12)"
                              : "rgba(0, 0, 0, 0.12)",
                          "& .MuiChip-label": {
                            px: 1.25,
                            py: 0.25,
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                      />
                      {getOtherCombinations().length > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -6,
                            right: -10,
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? theme.palette.info.dark
                                : theme.palette.info.main,
                            color: "white",
                            borderRadius: "5px",
                            minWidth: 38,
                            height: 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            border: "2px solid white",
                            px: 0.5,
                          }}
                        >
                          +{getOtherCombinations().length} more
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* Price and Actions */}
            <Box sx={{ mt: "auto" }}>
              {/* Price */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  mb: 2,
                  p: 1.5,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : "rgba(0, 0, 0, 0.02)",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    {product.Default_Inventory?.Total_MRP_Price >
                      product.Default_Inventory?.Total_Selling_Price && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          textDecoration: "line-through",
                          fontSize: "0.9rem",
                        }}
                      >
                        {context.state?.companyInfo?.Currency}{" "}
                        {product.Default_Inventory?.Total_MRP_Price}
                      </Typography>
                    )}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        fontSize: { xs: "1.3rem", sm: "1.5rem" },
                      }}
                    >
                      {context.state?.companyInfo?.Currency}{" "}
                      {product.Default_Inventory?.Total_Selling_Price}
                    </Typography>
                  </Box>

                  {/* Wishlist Button */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onToggleWishlist) {
                        onToggleWishlist(product);
                      }
                    }}
                    sx={{
                      color: isInWishlist(product)
                        ? "secondary.main"
                        : "text.secondary",
                      "&:hover": {
                        color: "secondary.main",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {isInWishlist(product) ? (
                      <FavoriteIcon />
                    ) : (
                      <FavoriteBorderIcon />
                    )}
                  </IconButton>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  onClick={(e) => {
                    e.stopPropagation();

                    // Use the actual Combination_Text from the default combination
                    const combinationText = product?.Default_Combination
                      ?.Combination_Text
                      ? product.Default_Combination.Combination_Text.toLowerCase().replace(
                          /[,\s]+/g,
                          "-"
                        ) // Replace commas and spaces with hyphens
                      : "default";

                    const combinationId =
                      product?.Default_Combination?.Combination_Id || "";

                    navigate(
                      `/ProductView/${product.Product_Full_Name.toLowerCase()}/${combinationText}/${combinationId}`
                    );
                    onClose();
                  }}
                  sx={{
                    flex: 1,
                    py: 1,
                    fontWeight: 600,
                    fontSize: "0.85rem",
                  }}
                >
                  View Details
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddShoppingCartIcon className="button-icon" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onAddToCart) {
                      onAddToCart(product);
                    }
                    onClose();
                  }}
                  sx={{
                    flex: 1,
                    py: 1,
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    borderWidth: "2px",
                    position: "relative",
                    overflow: "hidden",
                    background:
                      "linear-gradient(45deg, transparent, transparent)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: (theme) =>
                        `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.light} 100%)`,
                      transition: "left 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                      zIndex: -1,
                    },
                    "&:hover": {
                      color: "white",
                      borderColor: "transparent",
                      transform: "translateY(-1px)",
                      boxShadow: (theme) =>
                        `0 8px 20px ${theme.palette.primary.main}40, 0 4px 12px ${theme.palette.secondary.main}30`,
                      "&::before": {
                        left: 0,
                      },
                      "& .button-icon": {
                        transform: "translateX(4px) scale(1.1)",
                      },
                    },
                    "& .button-icon": {
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    },
                  }}
                >
                  Add to Cart
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProductQuickView;
